import React, { useState, useEffect, createContext, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Users from '../schemas/public/Users';
import { UserDataProp } from '../models/userDataProp';
import UserLoadingScreen from '../components/UserLoadingScreen';
import { getUser } from '../stores/User';
import { notifyUnavailable } from '../utilities/apiFailures';
import {
    exitActAsTokenOverlay,
    getAccessToken,
    getRefreshToken,
    isActAsSessionActive
} from '../utilities/storage/tokenStorage';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { runStorageLogout } from '../utilities/storage/storageDomains';
import { clearActAsMarker, getActAsMarker } from '../utilities/storage/actAsMarkerStorage';
import { useActAsMode } from './hooks/useActAsMode';
import { useNetworkRetry } from './hooks/useNetworkRetry';
import { signOutXimiSso } from '../stores/ximi/ximiStimulinSignOut';
import { reportXimiStimulinUsageIfDue } from '../stores/ximi/ximiStimulinUsage';
import { isXimiUser } from '../utilities/ximi/isXimiUser';
import { useAppSession } from '../hooks/useAppSession';
import useRouteActivity from '../hooks/useRouteActivity';
import { API_URL } from '../config';
import {
    XIMI_LOGIN_MANUAL_ENTRY_PATH,
    XIMI_LOGIN_SELECT_ACCOUNT_PATH,
    XIMI_SSO_CALLBACK_PATH
} from '../routePaths';

export const UserContext = createContext<UserDataProp>({
    user: null,
    setUser: () => {},
    mergeUserData: () => {},
    refreshUser: async (tokenOverride?: string) => null,
    token: '',
    logout: () => {},
    enterActAs: async () => false,
    exitActAs: async () => {},
    isActAsActive: false
});

interface UserProviderProps {
    children?: React.ReactNode;
}

export function UserProvider({ children }: UserProviderProps): JSX.Element {
    const [loaded, setLoaded] = useState<boolean | false>(false);
    // API injoignable au chargement de l'utilisateur : session gardée, init relancé au retour du réseau.
    const networkRetry = useNetworkRetry();
    const { t } = useTranslation();
    const history = useHistory();
    const { pathname } = useLocation();

    const [propData, setPropData] = useState<UserDataProp>({
        user: null,
        setUser: (userData: Users) => {
            setPropData((prev) => ({ ...prev, user: userData }));
        },
        mergeUserData: (updatedFields: Partial<Users>) => {
            setPropData((prev) => ({
                ...prev,
                user: prev.user ? { ...prev.user, ...updatedFields } : (updatedFields as Users)
            }));
        },
        token: '',
        refreshUser: async () => null,
        logout: () => {}
    });

    const { startSession: startAppSession, endSession: endAppSession } = useAppSession(
        propData.token
    );

    // Track time spent on important routes/areas (games / album)
    useRouteActivity(propData.token);

    const logout = useCallback(async () => {
        // Une déconnexion est toujours celle du compte porteur réel (le Pro) : on sort
        // d'abord de l'overlay act-as pour révoquer SA session — les sessions accompagnées
        // chaînées meurent d'elles-mêmes au renouvellement suivant (PARENT_REVOKED).
        exitActAsTokenOverlay();
        const refreshToken = await getRefreshToken();
        if (refreshToken) {
            // keepalive: on the web the SSO end_session redirect leaves the page right after, and
            // cancelled a plain request still in flight (the refresh token then stayed valid).
            fetch(`${API_URL}/auth/logout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken }),
                keepalive: true
            }).catch(() => {});
        }
        // end_session AVANT le nettoyage : il lit le user oidc (id_token_hint). Sur le web il quitte
        // la page, et lance donc lui-même le nettoyage avant de partir.
        await signOutXimiSso(runStorageLogout).catch(() => {}); // no-op si user classique
        await runStorageLogout();
        setPropData((prev) => ({ ...prev, token: '', user: null }));
        history.replace('/login');
    }, [history]);

    const refreshUser = useCallback(
        async (tokenOverride?: string): Promise<Users | null> => {
            // Use provided token or get the most current token from localStorage
            const currentToken = tokenOverride || (await getAccessToken()) || '';
            if (!currentToken) {
                console.warn('No token available for refreshUser');
                return null;
            }
            try {
                const updatedUser: Users | null = await getUser();
                if (updatedUser) {
                    setPropData((prev) => ({ ...prev, user: updatedUser, token: currentToken }));
                    // Fresh sign-in (Ximi SSO, login form): the init effect then finds this token
                    // already loaded and skips its own usage declaration.
                    if (tokenOverride) void reportXimiStimulinUsageIfDue();
                    return updatedUser;
                } else {
                    // If a token is found but no user
                    // Do not update state if tokenOverride is provided (login case)
                    // This allows the login function to handle the error itself
                    if (!tokenOverride) {
                        // If this is an automatic refresh (no tokenOverride), log out
                        logout();
                    } else {
                        // If this is a manual login, just set user to null to allow verification
                        setPropData((prev) => ({ ...prev, user: null, token: currentToken }));
                    }
                    return null;
                }
            } catch (error) {
                // getUser ne lève que sur un échec transitoire (réseau, 5xx, 429) : session gardée.
                console.error('Error refreshing user:', error);
                notifyUnavailable();
                if (tokenOverride) setPropData((prev) => ({ ...prev, user: null }));
                return null;
            }
        },
        [logout]
    );

    const setUser = useCallback((userData: Users) => {
        setPropData((prev) => ({ ...prev, user: userData }));
    }, []);

    const mergeUserData = useCallback((updatedFields: Partial<Users>) => {
        setPropData((prev) => ({
            ...prev,
            user: prev.user ? { ...prev.user, ...updatedFields } : (updatedFields as Users)
        }));
    }, []);

    const { enterActAs, exitActAs } = useActAsMode({ refreshUser, endAppSession, startAppSession });

    // Marqueur présent + overlay mémoire vide = l'app a redémarré pendant un accompagnement :
    // le Pro est revenu sur son compte, on le lui annonce (décision 6 du plan).
    useEffect(() => {
        const announceEndedActAs = async () => {
            if (isActAsSessionActive()) return;
            const marker = await getActAsMarker();
            if (!marker) return;
            await clearActAsMarker();
            toast.info(
                t('Vous êtes revenu sur votre espace. La session avec {{name}} est terminée.', {
                    name: marker.beneficiaryFirstName
                })
            );
        };
        void announceEndedActAs();
    }, [t]);

    // Update the propData with the latest functions
    useEffect(() => {
        setPropData((prev) => ({
            ...prev,
            refreshUser,
            logout,
            setUser,
            mergeUserData,
            enterActAs,
            exitActAs
        }));
    }, [refreshUser, logout, setUser, mergeUserData, enterActAs, exitActAs]);

    useEffect(() => {
        const init = async () => {
            const localToken = (await getAccessToken()) || '';

            if (!localToken) {
                if (
                    (pathname !== '/register' &&
                        pathname !== '/new-account' &&
                        pathname !== '/new-account-confirm' &&
                        pathname !== '/forgot-password' &&
                        pathname !== '/guide-utilisateurs' &&
                        pathname !== '/login' &&
                        pathname !== XIMI_SSO_CALLBACK_PATH &&
                        pathname !== XIMI_LOGIN_SELECT_ACCOUNT_PATH &&
                        pathname !== XIMI_LOGIN_MANUAL_ENTRY_PATH &&
                        pathname.slice(0, 7) !== '/login/') ||
                    pathname === '/login/me'
                ) {
                    history.replace('/login');
                    setLoaded(true);
                } else {
                    setLoaded(true);
                }
                return;
            }

            // If the token has changed or if we don't have a user yet
            if (localToken !== propData.token || !propData.user) {
                let updatedUser: Users | null;
                try {
                    updatedUser = await getUser();
                } catch {
                    networkRetry.setWaiting(true);
                    return;
                }
                networkRetry.setWaiting(false);
                if (!updatedUser) {
                    await logout();
                    // Already on /login, logout's redirect changes no path: nothing re-runs this
                    // effect, and the loader stayed.
                    setLoaded(true);
                    return;
                }
                setPropData((prev) => ({
                    ...prev,
                    token: localToken,
                    user: updatedUser
                }));
                setLoaded(true);
                // Launch / reload: declare today's Stimul'In usage to Ozmo (fresh sign-in: refreshUser).
                void reportXimiStimulinUsageIfDue();
                // User Ximi : identité gérée dans Ximi → pas de complétion de profil forcée.
                if (updatedUser && !isXimiUser(updatedUser)) {
                    if (
                        !updatedUser.firstName ||
                        !updatedUser.lastName ||
                        updatedUser.firstName === '' ||
                        updatedUser.lastName === ''
                    ) {
                        history.replace('/profile');
                    }
                }
            } else {
                networkRetry.setWaiting(false); // un essai concurrent a pu échouer après le succès
                setLoaded(true);
            }
        };
        init();
    }, [pathname, logout, networkRetry.attempt]);

    return loaded ? (
        <UserContext.Provider value={{ ...propData, isActAsActive: isActAsSessionActive() }}>
            {children}
        </UserContext.Provider>
    ) : (
        <UserLoadingScreen offline={networkRetry.waiting} />
    );
}
